import { useEffect, useState } from 'react';
import type { Idea, IdeaBuild } from '../api';
import * as api from '../api';
import { BuildBadge, enabledBuildRepos } from '../claudeBuilds';
import { ManualBuild } from './GithubManual';

// "Build with Claude" in the idea panel (P5-11b): start a build in a repo with
// Claude builds on, and see this idea's builds. Hidden unless builds are on
// somewhere (Pro, GitHub connected).

function when(sql: string): string {
  return new Date(sql.replace(' ', 'T') + 'Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function BuildSection({ idea, onChanged }: { idea: Idea; onChanged: () => void }) {
  const [repos, setRepos] = useState<string[]>([]);
  const [repo, setRepo] = useState('');
  const [builds, setBuilds] = useState<IdeaBuild[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState<api.BuildManualStep | null>(null);

  useEffect(() => {
    let live = true;
    enabledBuildRepos().then((r) => { if (live) { setRepos(r); setRepo((cur) => cur || r[0] || ''); } });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (repos.length === 0) return;
    let live = true;
    setBuilds([]);
    setError(null);
    setManual(null);
    api.listIdeaBuilds(idea.id).then((r) => { if (live) setBuilds(r.builds); }).catch(() => {});
    return () => { live = false; };
  }, [idea.id, repos.length]);

  if (repos.length === 0) return null;

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.startClaudeBuild(idea.id, repos.length > 1 ? repo : undefined);
      const build = r.build;
      if (build) setBuilds((cur) => [build, ...cur.filter((b) => b.id !== build.id)]);
      setManual(r.manual ?? null);
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="detail-section">
      <div className="detail-label">CLAUDE BUILDS</div>
      <div className="build-start">
        {repos.length > 1 && (
          <select className="link-type-select" aria-label="Repository" value={repo} onChange={(e) => setRepo(e.target.value)} disabled={busy}>
            {repos.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        )}
        <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => void start()}>
          {busy ? 'Starting…' : 'Build with Claude'}
        </button>
      </div>
      <p className="settings-hint">The <span className="mono">neuralrepo:build</span> label on the idea's GitHub issue starts your repo's Claude workflow. Claude pushes a branch and leaves a Create PR link on the issue; the pull request shows here once you open it.</p>
      {error && <p className="review-error" role="alert">{error}</p>}
      {manual && <ManualBuild step={manual} />}
      {builds.length > 0 && (
        <ul className="build-list">
          {builds.map((b) => (
            <li key={b.id}>
              <BuildBadge build={b} />
              <span className="build-list-meta">
                {when(b.requested_at)} · <a href={b.issue_url} target="_blank" rel="noopener noreferrer">{b.repo}#{b.issue_number}</a>
                {b.run_url && <> · <a href={b.run_url} target="_blank" rel="noopener noreferrer">run</a></>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
